// showcaseNudge (ADR-0045 §7, #215): the one-shot "you can turn me" hint. Right
// AFTER the reveal settles (onRevealComplete), the cushion plays a single gentle
// rotate-and-settle — a few degrees of yaw that eases OUT and back to the EXACT
// home orientation — then the loop goes quiet. This is the whole affordance: it
// signals the object-turntable interaction (ADR-0045 §1) without a continuous
// auto-spin, which would draw frames forever and drain the battery (idle stays at
// 0 frames, demand-render — ADR-0045 §7, the rejected turntable).
//
// PURE + GL-free so the beat is unit-testable (mirrors revealState / flip /
// objectRotation): a single lobe over an elapsed clock, eased at BOTH ends so it
// starts and lands with zero angular velocity (no jerk into or out of the hint),
// returning to exactly 0 so the cushion sits at the recognition pose afterwards.
// The GL side (ObjectGestureControls) owns the elapsed clock + the group write +
// the demand-render frames and reads `done` to know when to quiet the loop.
//
// Under prefers-reduced-motion the nudge is a NO-OP: it completes immediately with
// a 0 offset, so the caller applies nothing and never wakes the demand loop.

import { clamp01, smoothstep01 } from "./revealState";

// Peak yaw of the hint (radians) — a few degrees to one side and back. Big enough
// to read as "this turns", small enough to stay a gentle nudge, never a spin. A
// live sign-off tune (ADR-0045 notes), not frozen.
export const DEFAULT_NUDGE_ANGLE_RAD = (9 * Math.PI) / 180;

// The hint's whole wall-time (seconds): out to the peak and back. A calm beat, not
// a flick. Live tune.
export const DEFAULT_NUDGE_DURATION_SEC = 1.1;

export interface NudgeConfig {
  // Peak yaw offset (radians) of the out-and-back lobe.
  angleRad: number;
  // The whole nudge's duration (seconds).
  durationSec: number;
}

export const DEFAULT_NUDGE_CONFIG: NudgeConfig = {
  angleRad: DEFAULT_NUDGE_ANGLE_RAD,
  durationSec: DEFAULT_NUDGE_DURATION_SEC,
};

export interface NudgeOptions {
  // prefers-reduced-motion: the nudge does not play at all (ADR-0045 §7 / the
  // acceptance criterion) — it resolves to a 0 offset and reports done at once, so
  // the caller applies nothing and the idle demand loop is never woken.
  reducedMotion: boolean;
}

export interface NudgeSample {
  // The additive YAW offset (radians) to lay on top of the home orientation this
  // frame. Only yaw moves — a plain hint never rolls or tilts ("up is up",
  // ADR-0045 §2). Exactly 0 at the start, at the end, and forever after.
  yaw: number;
  // Whether the nudge has finished (elapsed past its duration, or immediately under
  // reduced motion). Once true it stays true — the hint plays ONCE. The caller stops
  // requesting frames when this flips, so the scene quiets to 0 frames.
  done: boolean;
}

// The out-and-back ENVELOPE: a single smooth lobe over normalized progress
// t ∈ [0, 1] — 0 at t ≤ 0, rising to 1 at the midpoint, back to 0 at t ≥ 1.
// `sin(π · smoothstep(t))` composes the sine half-arc with a smoothstep so BOTH
// ends have zero slope: the hint eases in from rest and lands back at rest with no
// velocity discontinuity (unlike a raw `sin(π t)`, which starts with a jerk). It is
// exactly 0 at both endpoints, so the cushion returns to its precise home yaw.
export function nudgeEnvelope(t: number): number {
  // Snap the endpoints to EXACTLY 0 (Math.sin(Math.PI) is 1.2e-16, not 0): the
  // acceptance criterion is a return to the EXACT home orientation, and this also
  // clamps any out-of-range t to the resting 0.
  if (t <= 0 || t >= 1) return 0;
  return Math.sin(Math.PI * smoothstep01(t));
}

// The nudge at `elapsedSec` since it began. Pure: the offset is a function of the
// elapsed clock + the config, so a monotonic clock plays the lobe once and then
// holds at rest (yaw 0, done). Under reduced motion it is a no-op that completes
// immediately, so the caller applies nothing and the idle loop stays quiet.
export function showcaseNudge(
  elapsedSec: number,
  config: NudgeConfig,
  options: NudgeOptions,
): NudgeSample {
  if (options.reducedMotion) return { yaw: 0, done: true };
  // Clamp only the DIVISOR against a zero/absurd duration (no div-by-zero); the
  // done test uses the real duration so a 0/absurd one resolves as done at once.
  const t = clamp01(elapsedSec / Math.max(config.durationSec, 0.0001));
  return {
    yaw: config.angleRad * nudgeEnvelope(t),
    // Past the duration the lobe is already back at 0; report done so the caller
    // can quiet the demand loop (and never replay — the hint plays once).
    done: elapsedSec >= Math.max(config.durationSec, 0),
  };
}
